import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../assets/stylesheets/CategoryLanding.css";

// ✅ ADD HEADER & FOOTER
import Header from "../components/Header";
import Footer from "../components/Footer";

import HeroZip from "./categoryLanding/HeroZip";
import ValueProps from "./categoryLanding/ValueProps";
import AdvantagesCards from "./categoryLanding/AdvantagesCards";
import BenefitsGrid from "./categoryLanding/BenefitsGrid";
import GalleryCarousel from "./categoryLanding/GalleryCarousel";
import ReviewsCarousel from "./categoryLanding/ReviewsCarousel";
import FAQAccordion from "./categoryLanding/FAQAccordion";

import FeaturedTool from "../components/tools/FeaturedTool";
import ToolsGrid from "../components/tools/ToolsGrid";
import FinancingWidget from "./FinancingWidget";
import {
  EVENT_NAMES,
  PARAM_KEYS,
  setPersistentContext,
  trackEvent,
} from "../analytics";
import { detectRegion } from "../services/locationApi";
import {
  clearDetectedLocation,
  getConfirmedLocationContext,
  getDetectedLocationContext,
  persistConfirmedLocation,
  persistDetectedLocation,
} from "../utils/locationStorage";
import { useBrand } from "../branding/BrandContext";
import { BrandMark, getBrandedPlainText } from "../branding/BrandText";

/**
 * WEBPACK-NATIVE CATEGORY CONTENT LOADER
 */
const categoriesContext = require.context(
  "../content/categories",
  false,
  /\.content\.json$/
);

const CATEGORY_DATA = categoriesContext.keys().reduce((acc, key) => {
  const slug = key.replace("./", "").replace(".content.json", "");
  acc[slug] = categoriesContext(key);
  return acc;
}, {});

const CATEGORY_SLUG_ALIASES = {
  ADUs: "adus",
  adu: "adus",
  "arborist-tree-services": "landscaping",
  concrete: "concrete-foundation",
  "exterior-door-replacement": "windows-replacement",
  "fence-install": "fence-gates",
  "foundation-repair": "concrete-foundation",
  gates: "fence-gates",
  "pool-maintenance": "pools",
};

function normalizeCategorySlug(slug) {
  return CATEGORY_SLUG_ALIASES[slug] || slug;
}

function normalizeRegionContextValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function isSantaClaritaRegionContext({ slug = "", name = "" } = {}) {
  const normalizedSlug = normalizeRegionContextValue(slug);
  const normalizedName = normalizeRegionContextValue(name);

  return (
    normalizedSlug === "santa clarita" ||
    normalizedSlug === "ca santa clarita" ||
    normalizedSlug === "santa clarita valley region" ||
    normalizedName === "santa clarita" ||
    normalizedName === "santa clarita valley region" ||
    normalizedSlug.includes("santa clarita") ||
    normalizedName.includes("santa clarita")
  );
}

const CAMPAIGN_STORAGE_KEY = "homehelper_campaign_id";
const CAMPAIGN_TTL_MS = 24 * 60 * 60 * 1000;
const REGION_PROXIMITY_STORAGE_KEY = "homehelper_region_proximity";
const REGION_SLUG_STORAGE_KEY = "homehelper_region_slug";
const REGION_NAME_STORAGE_KEY = "homehelper_region_name";
const ZIP_REGEX = /^\d{5}$/;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.home-helper.com";
const FEATURED_CONTRACTOR_ENDPOINT =
  `${API_BASE_URL}/api/featured-contractor`;
const REGION_DEFAULT_ZIP_ENDPOINT = `${API_BASE_URL}/api/zipcodes/region-default`;
const FEATURED_CONTRACTOR_RETRY_DELAY_MS = 400;

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function persistZipToStorage(zipValue) {
  if (!ZIP_REGEX.test(zipValue || "")) return;

  try {
    localStorage.setItem("hh_zip", zipValue);
    localStorage.setItem("user_zip", zipValue);
  } catch (err) {
    // ignore storage errors
  }
}

function normalizeZip(value) {
  const zip = String(value || "").trim();
  return ZIP_REGEX.test(zip) ? zip : "";
}

function buildRegionProximityQuestion(regionName) {
  const normalizedRegionName = String(regionName || "").trim();

  if (!normalizedRegionName) {
    return "Are you within 30 minutes of this service region?";
  }

  if (/service region$/i.test(normalizedRegionName)) {
    return `Are you within 30 minutes of the ${normalizedRegionName}?`;
  }

  return `Are you within 30 minutes of the ${normalizedRegionName} service region?`;
}

function formatDetectedLocation(detected = {}) {
  const city = String(detected?.city || "").trim();
  const state = String(detected?.state || "").trim();
  const zip = normalizeZip(detected?.zip || "");
  const parts = [];

  if (city) {
    parts.push(city);
  }

  if (state) {
    parts.push(state);
  }

  const location = parts.join(", ");
  if (location && zip) {
    return `${location} ${zip}`;
  }

  return location || zip || "";
}

function buildFallbackMessage(reason, brand) {
  const brandText = (message) => getBrandedPlainText(message, brand);

  switch (String(reason || "").trim()) {
    case "out_of_pilot":
      return brandText("Home-Helper is not available in your area yet. Enter your ZIP code to check again.");
    case "zip_outside_santa_clarita":
      return brandText("We detected a location outside the Santa Clarita Valley service region. Enter a Santa Clarita Valley ZIP code to continue.");
    case "region_not_for_sale":
    case "region_inactive":
    case "region_category_not_sellable":
    case "CATEGORY_NOT_FOR_SALE":
    case "CATEGORY_INACTIVE":
      return "That area is not currently available for this project type. Enter your ZIP code to find the right service region.";
    case "private_ip":
    case "disabled":
    case "missing_credentials":
    case "no_usable_location":
    case "unresolved":
    default:
      return brandText("Enter your ZIP code so we can match you with the right Home-Helper service region.");
  }
}

function storeCampaignId(cid) {
  if (!cid) return;
  localStorage.setItem(
    CAMPAIGN_STORAGE_KEY,
    JSON.stringify({
      value: cid,
      expiresAt: Date.now() + CAMPAIGN_TTL_MS,
    })
  );
}

export default function CategoryLanding() {
  const brand = useBrand();
  const params = useParams();
  const navigate = useNavigate();
  const rawSlug = params?.category;
  const slug = normalizeCategorySlug(rawSlug);
  const zipParam = params?.zip;
  const isValidZip = ZIP_REGEX.test(zipParam);
  const regionSlugParam = zipParam && !isValidZip ? zipParam : "";
  const confirmedLocation = useMemo(() => getConfirmedLocationContext(), []);
  const detectedLocation = useMemo(() => getDetectedLocationContext(), []);
  const initialLocation = confirmedLocation.regionSlug || confirmedLocation.zip
    ? confirmedLocation
    : detectedLocation;
  const [zip, setZip] = useState(() => {
    if (isValidZip) {
      return zipParam;
    }

    return initialLocation.zip || "";
  });
  const [regionName, setRegionName] = useState(
    () => initialLocation.regionName || ""
  );
  const [regionSlug, setRegionSlug] = useState(
    () => regionSlugParam || initialLocation.regionSlug || ""
  );
  const [regionId, setRegionId] = useState(
    () => initialLocation.regionId || ""
  );
  const [locationSource, setLocationSource] = useState(
    () => initialLocation.source || ""
  );
  const [locationConfidence, setLocationConfidence] = useState(
    () => initialLocation.confidence || ""
  );
  const [heroMode, setHeroMode] = useState(() => {
    if (
      isValidZip ||
      regionSlugParam ||
      confirmedLocation.regionSlug ||
      confirmedLocation.zip ||
      detectedLocation.regionSlug ||
      detectedLocation.zip
    ) {
      return "proximity";
    }

    return "detecting";
  });
  const [heroMessage, setHeroMessage] = useState("");
  const [heroError, setHeroError] = useState("");
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityDetectedLocation, setAvailabilityDetectedLocation] = useState("");

  useEffect(() => {
    if (!rawSlug || rawSlug === slug) return;

    const nextPath = params?.zip
      ? `/${params.zip}/${slug}`
      : `/in-town/${slug}`;

    navigate(nextPath, { replace: true });
  }, [navigate, params?.zip, rawSlug, slug]);
  const [heroLoading, setHeroLoading] = useState(false);
  const [featuredContractor, setFeaturedContractor] = useState(null);
  const detectionInitRef = useRef(false);
  const lastRegionLookupKeyRef = useRef("");
  const lastFeaturedLookupKeyRef = useRef("");

  const regionLookupKey = useMemo(() => {
    if (isValidZip) {
      return `zip:${zipParam}`;
    }

    if (regionSlugParam) {
      return `slug:${regionSlugParam}`;
    }

    if (confirmedLocation.regionSlug) {
      return `slug:${confirmedLocation.regionSlug}`;
    }

    if (confirmedLocation.zip) {
      return `zip:${confirmedLocation.zip}`;
    }

    if (detectedLocation.regionSlug) {
      return `slug:${detectedLocation.regionSlug}`;
    }

    if (detectedLocation.zip) {
      return `zip:${detectedLocation.zip}`;
    }

    return "";
  }, [
    confirmedLocation.regionSlug,
    confirmedLocation.zip,
    detectedLocation.regionSlug,
    detectedLocation.zip,
    isValidZip,
    regionSlugParam,
    zipParam,
  ]);

  const contractorLookupZip = useMemo(() => {
    if (isValidZip && zipParam) {
      return zipParam;
    }

    return ZIP_REGEX.test(zip) ? zip : "";
  }, [isValidZip, zip, zipParam]);

  const featuredContractorLookupKey = useMemo(() => {
    if (!slug || !contractorLookupZip) {
      return "";
    }

    return `${slug}:${contractorLookupZip}`;
  }, [contractorLookupZip, slug]);
  
  useEffect(() => {
    if (!isValidZip) {
      return;
    }

    setZip(zipParam);
    persistZipToStorage(zipParam);
    persistConfirmedLocation({
      regionId: confirmedLocation.regionId || "",
      regionSlug: regionSlugParam || confirmedLocation.regionSlug || "",
      regionName: confirmedLocation.regionName || "",
      zip: zipParam,
      source: confirmedLocation.source || "route_zip",
      confidence: confirmedLocation.confidence || "confirmed",
    });
  }, [
    confirmedLocation.confidence,
    confirmedLocation.regionId,
    confirmedLocation.regionName,
    confirmedLocation.regionSlug,
    confirmedLocation.source,
    isValidZip,
    regionSlugParam,
    zipParam,
  ]);

  useEffect(() => {
    setRegionName(confirmedLocation.regionName || detectedLocation.regionName || "");
    setRegionSlug(
      regionSlugParam ||
        confirmedLocation.regionSlug ||
        detectedLocation.regionSlug ||
        ""
    );
    setRegionId(confirmedLocation.regionId || detectedLocation.regionId || "");
    setLocationSource(confirmedLocation.source || detectedLocation.source || "");
    setLocationConfidence(
      confirmedLocation.confidence || detectedLocation.confidence || ""
    );
    setHeroError("");
    setHeroMessage(
      getBrandedPlainText(
        "We use your approximate location to match you with the closest Home-Helper service region.",
        brand
      )
    );
    setHeroMode(
      isValidZip ||
        regionSlugParam ||
        confirmedLocation.regionSlug ||
        confirmedLocation.zip ||
        detectedLocation.regionSlug ||
        detectedLocation.zip
        ? "proximity"
        : "detecting"
    );
    detectionInitRef.current = false;
    lastRegionLookupKeyRef.current = "";
    lastFeaturedLookupKeyRef.current = "";
    setFeaturedContractor(null);
  }, [
    confirmedLocation.confidence,
    confirmedLocation.regionId,
    confirmedLocation.regionName,
    confirmedLocation.regionSlug,
    confirmedLocation.source,
    confirmedLocation.zip,
    detectedLocation.confidence,
    detectedLocation.regionId,
    detectedLocation.regionName,
    detectedLocation.regionSlug,
    detectedLocation.source,
    detectedLocation.zip,
    isValidZip,
    regionSlugParam,
    slug,
    zipParam,
    brand,
  ]);

  useEffect(() => {
    if (!regionLookupKey) return;
    if (lastRegionLookupKeyRef.current === regionLookupKey) return;

    lastRegionLookupKeyRef.current = regionLookupKey;
    const controller = new AbortController();

    const fetchRegionContext = async () => {
      try {
        const searchParams = new URLSearchParams();

        if (regionLookupKey.startsWith("slug:")) {
          searchParams.set("slug", regionLookupKey.slice(5));
        } else if (regionLookupKey.startsWith("zip:")) {
          searchParams.set("zip", regionLookupKey.slice(4));
        } else {
          return;
        }

        const res = await fetch(`${REGION_DEFAULT_ZIP_ENDPOINT}?${searchParams.toString()}`, {
          signal: controller.signal,
          cache: "no-store",
        });

        if (!res.ok) {
          throw new Error(`Region default ZIP lookup failed with ${res.status}`);
        }

        const data = await res.json();
        const defaultZip = String(data?.zip_code || "").trim();
        const nextRegionName = String(data?.region_name || "").trim();
        const nextRegionSlug = String(data?.region_slug || "").trim();
        const nextStoredRegionName =
          nextRegionName ||
          confirmedLocation.regionName ||
          detectedLocation.regionName ||
          "";
        const nextStoredRegionSlug =
          nextRegionSlug ||
          regionSlugParam ||
          confirmedLocation.regionSlug ||
          detectedLocation.regionSlug ||
          "";

        setRegionName((prev) => (prev === nextStoredRegionName ? prev : nextStoredRegionName));
        setRegionSlug((prev) => (prev === nextStoredRegionSlug ? prev : nextStoredRegionSlug));
        setRegionId((prev) => (prev === String(data?.region_id || "") ? prev : String(data?.region_id || "")));

        if (!ZIP_REGEX.test(defaultZip)) return;

        setZip((prev) => (prev === defaultZip ? prev : defaultZip));
        if (
          (
            isValidZip ||
            confirmedLocation.regionSlug ||
            confirmedLocation.zip ||
            [
              "manual_zip",
              "route_zip",
              "confirmed",
              "confirmed_location",
              "category_landing_confirmed",
            ].includes(locationSource)
          ) &&
          (nextStoredRegionSlug || defaultZip)
        ) {
          persistZipToStorage(defaultZip);
          persistConfirmedLocation({
            regionId: String(data?.region_id || regionId || ""),
            regionSlug: nextStoredRegionSlug,
            regionName: nextStoredRegionName,
            zip: defaultZip,
            source: locationSource || "confirmed_location",
            confidence: locationConfidence || "confirmed",
          });
        }
        setPersistentContext({
          [PARAM_KEYS.ZIP_CODE]: defaultZip,
        });
      } catch (err) {
        if (!controller.signal.aborted) {
          console.warn("[CategoryLanding] Region default ZIP lookup failed", err);
        }
      }
    };

    fetchRegionContext();

    return () => {
      controller.abort();
    };
  }, [
    confirmedLocation.regionName,
    confirmedLocation.regionSlug,
    detectedLocation.regionName,
    detectedLocation.regionSlug,
    heroMode,
    locationConfidence,
    locationSource,
    regionId,
    regionLookupKey,
    regionSlugParam,
  ]);

  useEffect(() => {
    if (
      isValidZip ||
      regionSlugParam ||
      confirmedLocation.regionSlug ||
      confirmedLocation.zip ||
      detectedLocation.regionSlug ||
      detectedLocation.zip
    ) {
      return;
    }

    if (detectionInitRef.current) {
      return;
    }

    detectionInitRef.current = true;
    const controller = new AbortController();

    const hydrateDetectedLocation = async () => {
      setHeroMode("detecting");
      setHeroError("");
      setShowAvailabilityModal(false);
      setAvailabilityDetectedLocation("");
      setHeroMessage(
        "We use your approximate location to help match you with the right local service region. You can change it anytime."
      );

      try {
        const payload = await detectRegion({
          categorySlug: slug,
          signal: controller.signal,
        });

        const nextRegion = payload?.region || null;
        const fallbackReason = payload?.fallback?.reason || "";
        const nextZip = normalizeZip(payload?.detected?.zip || nextRegion?.representativeZip || "");
        const nextSource = payload?.source || "detected";
        const nextConfidence = payload?.confidence || "";
        const detectedLocationLabel = formatDetectedLocation(payload?.detected);

        if (
          nextRegion &&
          !payload?.fallback?.askForZip &&
          ["high", "medium"].includes(String(nextConfidence).toLowerCase())
        ) {
          persistDetectedLocation({
            regionId: nextRegion.id || "",
            regionSlug: nextRegion.slug || "",
            regionName: nextRegion.name || "",
            zip: nextZip,
            source: nextSource,
            confidence: nextConfidence,
            requiresConfirmation: false,
          });
          setRegionId(String(nextRegion.id || ""));
          setRegionSlug(nextRegion.slug || "");
          setRegionName(nextRegion.name || "");
          setZip(nextZip);
          setLocationSource(nextSource);
          setLocationConfidence(nextConfidence);
          setHeroMessage(
            getBrandedPlainText(
              "We use your approximate location to match you with the closest Home-Helper service region.",
              brand
            )
          );
          setHeroMode("proximity");
          setPersistentContext({
            [PARAM_KEYS.ZIP_CODE]: nextZip || undefined,
          });
          return;
        }

        clearDetectedLocation();
        setZip((prev) => (prev === nextZip ? prev : nextZip));
        setHeroMode("zip");
        setAvailabilityDetectedLocation(detectedLocationLabel);
        setHeroMessage(buildFallbackMessage(fallbackReason, brand));
        if (fallbackReason === "zip_outside_santa_clarita") {
          setShowAvailabilityModal(true);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          clearDetectedLocation();
          setHeroMode("zip");
          setHeroMessage(buildFallbackMessage("unresolved", brand));
        }
      }
    };

    hydrateDetectedLocation();

    return () => {
      controller.abort();
    };
  }, [
    confirmedLocation.regionSlug,
    confirmedLocation.zip,
    detectedLocation.regionSlug,
    detectedLocation.zip,
    isValidZip,
    regionSlugParam,
    slug,
    brand,
  ]);

  const campaignInitRef = useRef(false);

  useEffect(() => {
    if (campaignInitRef.current) return;
    campaignInitRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const cid = params.get("cid");
    if (!cid) return;

    storeCampaignId(cid);
    setPersistentContext({
      [PARAM_KEYS.CAMPAIGN_ID]: cid,
    });
    trackEvent(EVENT_NAMES.CAMPAIGN_VISIT, {
      [PARAM_KEYS.CAMPAIGN_ID]: cid,
    });
  }, []);

  const page = CATEGORY_DATA[slug];
  useEffect(() => {
    if (!featuredContractorLookupKey) return;
    if (lastFeaturedLookupKeyRef.current === featuredContractorLookupKey) return;

    lastFeaturedLookupKeyRef.current = featuredContractorLookupKey;

    const activeZip = contractorLookupZip;
    if (!activeZip || !slug) return;

    console.log("📡 Fetching featured contractor:", { zip: activeZip, slug });

    const controller = new AbortController();

    const fetchFeatured = async () => {
      const params = new URLSearchParams({ zip: activeZip, category: slug });
      const requestUrl = `${FEATURED_CONTRACTOR_ENDPOINT}?${params.toString()}`;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await fetch(requestUrl, {
            signal: controller.signal,
            cache: "no-store",
          });

          if (!res.ok) {
            throw new Error(`Featured contractor API failed with ${res.status}`);
          }

          const data = await res.json();

          setFeaturedContractor((prev) => {
            const next = data?.is_featured ? data.contractor : null;
            return prev === next ? prev : next;
          });
          return;
        } catch (err) {
          if (controller.signal.aborted) {
            return;
          }

          if (attempt === 0) {
            console.warn(
              "Featured contractor fetch failed on first attempt, retrying",
              err
            );
            await wait(FEATURED_CONTRACTOR_RETRY_DELAY_MS);
            continue;
          }

          console.error("Featured contractor fetch failed", err);
          setFeaturedContractor(null);
        }
      }
    };

    fetchFeatured();

    return () => {
      controller.abort();
    };
  }, [contractorLookupZip, featuredContractorLookupKey, slug]);

  if (!page) {
    return (
      <>
        <Header />
        <div style={{ padding: 40 }}>
          <h1>Category not found</h1>
          <p>
            No category <code>.content.json</code> found for slug: <b>{slug}</b>
          </p>
        </div>
        <Footer />
      </>
    );
  }

  const handleProximitySubmit = (proximityPayload) => {
    const openAvailabilityModal = () => {
      setHeroMode("zip");
      setHeroError("");
      setHeroMessage(
        getBrandedPlainText(
          "Enter your ZIP code so we can match you with the right Home-Helper service region.",
          brand
        )
      );
      setShowAvailabilityModal(true);
    };

    const normalizedPayload = {
      answer: proximityPayload?.answer || "",
      livesWithinRegionDriveTime:
        typeof proximityPayload?.lives_within_region_drive_time === "boolean"
          ? proximityPayload.lives_within_region_drive_time
          : null,
      regionSlug:
        proximityPayload?.region_slug ||
        regionSlug ||
        confirmedLocation.regionSlug ||
        detectedLocation.regionSlug ||
        "",
      regionName:
        proximityPayload?.region_name_snapshot ||
        regionName ||
        confirmedLocation.regionName ||
        detectedLocation.regionName ||
        "",
      regionProximityQuestion:
        proximityPayload?.region_proximity_question ||
        buildRegionProximityQuestion(
          proximityPayload?.region_name_snapshot ||
            regionName ||
            confirmedLocation.regionName ||
            detectedLocation.regionName ||
            ""
        ),
    };

    if (normalizedPayload.answer === "no") {
      setHeroMode("zip");
      setHeroError("");
      setHeroMessage(
        getBrandedPlainText(
          "Enter your ZIP code so we can match you with the right Home-Helper service region.",
          brand
        )
      );

      try {
        localStorage.setItem(
          REGION_PROXIMITY_STORAGE_KEY,
          JSON.stringify({
            answer: "no",
            lives_within_region_drive_time: false,
            region_slug: normalizedPayload.regionSlug || null,
            region_name_snapshot: normalizedPayload.regionName || null,
            region_proximity_question:
              normalizedPayload.regionProximityQuestion || null,
          })
        );
      } catch (err) {
        // ignore storage errors
      }

      return;
    }

    if (
      !isSantaClaritaRegionContext({
        slug: normalizedPayload.regionSlug,
        name: normalizedPayload.regionName,
      })
    ) {
      openAvailabilityModal();
      return;
    }

    try {
      persistConfirmedLocation({
        regionId,
        regionSlug: normalizedPayload.regionSlug,
        regionName: normalizedPayload.regionName,
        zip,
        source: locationSource || "category_landing_confirmed",
        confidence: locationConfidence || "confirmed",
      });
      localStorage.setItem(
        REGION_PROXIMITY_STORAGE_KEY,
        JSON.stringify({
          answer: normalizedPayload.answer || null,
          lives_within_region_drive_time:
            normalizedPayload.livesWithinRegionDriveTime,
          region_slug: normalizedPayload.regionSlug || null,
          region_name_snapshot: normalizedPayload.regionName || null,
          region_proximity_question:
            normalizedPayload.regionProximityQuestion || null,
        })
      );
      if (normalizedPayload.regionSlug) {
        localStorage.setItem(REGION_SLUG_STORAGE_KEY, normalizedPayload.regionSlug);
      }
      if (normalizedPayload.regionName) {
        localStorage.setItem(REGION_NAME_STORAGE_KEY, normalizedPayload.regionName);
      }
    } catch (err) {
      // ignore storage errors
    }

    trackEvent(EVENT_NAMES.CTA_CLICK, {
      [PARAM_KEYS.CTA_NAME]: "start_survey",
      [PARAM_KEYS.PLACEMENT]: "category_landing_hero",
      [PARAM_KEYS.CATEGORY_KEY]: slug,
      [PARAM_KEYS.ZIP_CODE]: zip || undefined,
    });

    navigate(`/survey/${slug}`, {
      state: {
        zip: ZIP_REGEX.test(zip) ? zip : "",
        source: locationSource || "category-landing",
        regionSlug: normalizedPayload.regionSlug || null,
        regionName: normalizedPayload.regionName || null,
        proximityAnswer: normalizedPayload.answer || null,
        livesWithinRegionDriveTime:
          normalizedPayload.livesWithinRegionDriveTime,
        regionProximityQuestion:
          normalizedPayload.regionProximityQuestion || null,
      }
    });
  };

  const handleZipSubmit = async (inputZip) => {
    const normalizedZip = normalizeZip(inputZip);
    const preserveProximityMode = heroMode === "proximity";

    if (!normalizedZip) {
      setHeroError("Please enter a valid 5-digit ZIP code.");
      if (!preserveProximityMode) {
        setHeroMode("zip");
      }
      return;
    }

    setHeroLoading(true);
    setHeroError("");

    try {
      const payload = await detectRegion({
        categorySlug: slug,
        zip: normalizedZip,
      });

      if (
        !payload?.region ||
        payload?.fallback?.askForZip ||
        !["high", "medium"].includes(String(payload?.confidence || "").toLowerCase())
      ) {
        setHeroMode("zip");
        setHeroMessage(buildFallbackMessage(payload?.fallback?.reason || "zip_unresolved", brand));
        setHeroError("");
        setShowAvailabilityModal(true);
        return;
      }

      const nextRegion = payload.region;
      const nextZip = normalizeZip(
        payload?.detected?.zip || payload?.region?.representativeZip || normalizedZip
      );

      clearDetectedLocation();
      persistConfirmedLocation({
        regionId: nextRegion.id || "",
        regionSlug: nextRegion.slug || "",
        regionName: nextRegion.name || "",
        zip: nextZip,
        source: "manual_zip",
        confidence: payload.confidence || "confirmed",
      });

      setRegionId(String(nextRegion.id || ""));
      setRegionSlug(nextRegion.slug || "");
      setRegionName(nextRegion.name || "");
      setZip(nextZip);
      setLocationSource("manual_zip");
      setLocationConfidence(payload.confidence || "confirmed");
      setHeroMode("proximity");
      setHeroMessage(getBrandedPlainText(
        "We matched your ZIP code to the right Home-Helper service region.",
        brand
      ));
      navigate(`/${nextRegion.slug || nextZip}/${slug}`, {
        replace: true,
        state: {
          zip: nextZip,
          regionSlug: nextRegion.slug || "",
          regionName: nextRegion.name || "",
          source: "manual_zip",
        },
      });
    } catch (error) {
      if (!preserveProximityMode) {
        setHeroMode("zip");
      }
      setHeroMessage(buildFallbackMessage("unresolved", brand));
      setHeroError("We couldn't check that ZIP code right now. Please try again.");
    } finally {
      setHeroLoading(false);
    }
  };

  return (
    <>
      <Header />

      <div className="categoryLanding">
        <HeroZip
          hero={page.hero}
          breadcrumbs={page.breadcrumbs}
          mode={heroMode}
          regionName={regionName}
          zipValue={zip}
          loading={heroLoading}
          errorMessage={heroError}
          helperMessage={heroMessage}
          questionText={buildRegionProximityQuestion(regionName)}
          onSubmit={handleProximitySubmit}
          onZipSubmit={handleZipSubmit}
        />

        {/* ⭐ FEATURED CONTRACTOR CTA */}
        {featuredContractor && (
          <div className="featuredContractorCard">
            <div className="featuredContractorInner">
              <div className="featuredContractorImage">
                <img
                  src={featuredContractor.image_url}
                  alt={featuredContractor.company_name}
                  onError={(e) => {
                    e.target.src =
                      "/assets/jim-featured-contractor.png";
                  }}
                />
              </div>

              <div className="featuredContractorContent">
                <h3>Featured Contractor</h3>
                <h2>{featuredContractor.company_name}</h2>

                <p>
                  {featuredContractor.tagline ||
                    `Top-rated ${slug} contractor near you`}
                </p>
              </div>
              
              <button
                className="primaryButton featuredContractorButton"
                onClick={() => {
                  if (!featuredContractor.provider_slug) {
                    console.error("❌ Missing provider_slug");
                    return;
                  }

                  trackEvent(EVENT_NAMES.FEATURED_CONTRACTOR_CLICK, {
                    [PARAM_KEYS.PROVIDER_ID]: featuredContractor.provider_slug,
                    [PARAM_KEYS.CATEGORY_KEY]: slug,
                    [PARAM_KEYS.ZIP_CODE]: zip,
                    [PARAM_KEYS.PLACEMENT]: "category_landing_featured_contractor",
                  });

                  navigate(
                    `/providers/${featuredContractor.provider_slug}`
                  );
                }}
              >
                View Profile
              </button>
            </div>
          </div>
        )}

        <ValueProps 
          featuredContractor={featuredContractor}
          heading={page.valueProps?.heading}
          bullets={page.valueProps?.bullets}
          image={page.valueProps?.image}
        />

        {page.featuredTool && (
          <div className="featuredToolBand">
            <FeaturedTool
              tool={page.featuredTool}
              category={slug}
              zip={zip}
            />
          </div>
        )}

        <AdvantagesCards
          heading={getBrandedPlainText("Why Homeowners Choose Home-Helper", brand)}
          subheading={page.advantages?.subheading}
          cards={page.advantages?.cards}
        />

        <BenefitsGrid
          heading={page.benefits?.heading}
          items={page.benefits?.items}
        />

        <GalleryCarousel
          heading={page.gallery?.heading}
          images={page.gallery?.images}
        />

        <ReviewsCarousel
          heading={page.reviews?.heading}
          reviews={page.reviews?.items}
        />

        <FAQAccordion
          heading={page.faq?.heading}
          items={page.faq?.items}
        />

        <div className="container">
          <FinancingWidget zip={zip} category={slug} />
        </div>

        <div className="container">
          <div>
            <ToolsGrid
              category={slug}
              zip={zip}
              subheading={`Explore tools to help plan and prepare your ${page.hero?.title?.toLowerCase()} project.`}
            />
          </div>
        </div>
      </div>

      {showAvailabilityModal && (
        <div
          className="heroModalOverlay"
          onClick={() => setShowAvailabilityModal(false)}
        >
          <div
            className="heroModalContent heroModalContent--availability"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="availability-modal-title"
          >
            <h3 id="availability-modal-title">Currently available only in Santa Clarita Valley</h3>
            <p>
              {availabilityDetectedLocation
                ? `We detected ${availabilityDetectedLocation}. `
                : ""}
              <BrandMark /> is currently available only in the Santa Clarita Valley region.
              Please enter a Santa Clarita Valley ZIP code to continue.
            </p>
            <button
              className="heroModalClose"
              onClick={() => setShowAvailabilityModal(false)}
              type="button"
            >
              Enter another ZIP code
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          maxWidth: 900,
          margin: "30px",
          fontSize: 14,
          color: "#6b7280",
          lineHeight: 1.6,
          textAlign: "center",
          justifySelf: "center",
        }}
      >
        <strong>Disclaimer</strong>
        <p className="cl-heroDisclaimer">
          <BrandMark domain /> provides informational tools, estimates, and guidance to help
          homeowners understand home improvement projects. Information presented on
          this website is for general informational purposes only and does not
          constitute professional, legal, financial, or construction advice.{" "}
          <BrandMark domain /> does not perform construction services and does not guarantee
          pricing, timelines, code compliance, or contractor performance. All project
          decisions, inspections, permits, and work should be reviewed and completed
          by properly licensed professionals in accordance with local regulations.
          Use of this website constitutes acceptance of these terms.
        </p>
      </div>

      <Footer />
    </>
  );
}
